import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  Users,
  TrendingUp,
  Mail,
  Clock,
  Eye,
  ArrowUpRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Compliance() {
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  useEffect(() => {
    fetchCompliance();
  }, [selectedMonth]);

  const fetchCompliance = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/compliance?month=${selectedMonth}`);
      setCompliance(response.data);
    } catch (error) {
      console.error("Failed to fetch compliance:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        value: date.toISOString().slice(0, 7),
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });
    }
    return months;
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || "??";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Compliance Tracking
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Monitor monthly session completion requirements
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger data-testid="month-select" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {generateMonthOptions().map((month) => (
                <SelectItem key={month.value} value={month.value}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards - Clickable */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/admin/users">
          <Card className="stats-card cursor-pointer group hover:shadow-xl hover:border-primary/20 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5C5C5C] flex items-center gap-1">
                    Total Users
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </p>
                  <p className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {compliance?.total_users || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Users className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Card 
          className="stats-card cursor-pointer group hover:shadow-xl hover:border-[#059669]/20 transition-all"
          onClick={() => document.querySelector('[data-testid="tab-compliant"]')?.click()}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C5C5C] flex items-center gap-1">
                  Compliant
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </p>
                <p className="text-3xl font-bold text-[#059669]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {compliance?.compliant_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#059669]/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                <CheckCircle className="w-6 h-6 text-[#059669]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="stats-card cursor-pointer group hover:shadow-xl hover:border-[#DC2626]/20 transition-all"
          onClick={() => document.querySelector('[data-testid="tab-non-compliant"]')?.click()}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C5C5C] flex items-center gap-1">
                  Non-Compliant
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </p>
                <p className="text-3xl font-bold text-[#DC2626]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {compliance?.non_compliant_count || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#DC2626]/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                <XCircle className="w-6 h-6 text-[#DC2626]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C5C5C]">Compliance Rate</p>
                <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {compliance?.compliance_rate || 0}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Compliance Progress */}
      <Card className="stats-card">
        <CardContent className="py-6">
          <div className="flex items-center gap-4 mb-4">
            <span className="text-sm font-medium text-[#5C5C5C]">Overall Compliance</span>
            <span className="text-sm font-bold text-primary">{compliance?.compliance_rate || 0}%</span>
          </div>
          <Progress value={compliance?.compliance_rate || 0} className="h-3" />
          <div className="flex justify-between mt-2 text-xs text-[#9CA3AF]">
            <span>0%</span>
            <span>Target: 100%</span>
          </div>
        </CardContent>
      </Card>

      {/* User Lists */}
      <Card className="stats-card">
        <CardContent className="p-0">
          <Tabs defaultValue="non-compliant" className="w-full">
            <TabsList className="w-full rounded-none border-b bg-transparent p-0">
              <TabsTrigger 
                value="non-compliant" 
                data-testid="tab-non-compliant"
                className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-[#DC2626] data-[state=active]:bg-transparent py-4"
              >
                <AlertTriangle className="w-4 h-4 mr-2 text-[#DC2626]" />
                Non-Compliant ({compliance?.non_compliant_count || 0})
              </TabsTrigger>
              <TabsTrigger 
                value="compliant" 
                data-testid="tab-compliant"
                className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-[#059669] data-[state=active]:bg-transparent py-4"
              >
                <CheckCircle className="w-4 h-4 mr-2 text-[#059669]" />
                Compliant ({compliance?.compliant_count || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="non-compliant" className="p-6">
              {compliance?.non_compliant_users?.length > 0 ? (
                <div className="space-y-3">
                  {compliance.non_compliant_users.map((user) => (
                    <div 
                      key={user.id}
                      className="flex items-center gap-4 p-4 bg-[#DC2626]/5 border border-[#DC2626]/20 rounded-xl"
                      data-testid={`non-compliant-user-${user.id}`}
                    >
                      <Avatar className="w-12 h-12 bg-[#DC2626] text-white">
                        <AvatarFallback className="bg-[#DC2626] text-white font-semibold">
                          {getInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#1A1A1A] truncate">{user.name}</p>
                        <p className="text-sm text-[#5C5C5C]">{user.department}</p>
                      </div>
                      <div className="text-right">
                        {user.days_since_last !== null ? (
                          <>
                            <p className="text-sm font-medium text-[#DC2626] flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {user.days_since_last} days ago
                            </p>
                            <p className="text-xs text-[#9CA3AF]">Last session</p>
                          </>
                        ) : (
                          <p className="text-sm text-[#9CA3AF]">No sessions yet</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#DC2626] hover:bg-[#DC2626]/10 rounded-full"
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Remind
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-[#059669] mx-auto mb-4" />
                  <p className="text-[#1A1A1A] font-semibold">Everyone is compliant!</p>
                  <p className="text-sm text-[#5C5C5C]">All users have completed their sessions this month</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="compliant" className="p-6">
              {compliance?.compliant_users?.length > 0 ? (
                <div className="space-y-3">
                  {compliance.compliant_users.map((user) => (
                    <div 
                      key={user.id}
                      className="flex items-center gap-4 p-4 bg-[#059669]/5 border border-[#059669]/20 rounded-xl"
                      data-testid={`compliant-user-${user.id}`}
                    >
                      <Avatar className="w-12 h-12 bg-[#059669] text-white">
                        <AvatarFallback className="bg-[#059669] text-white font-semibold">
                          {getInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#1A1A1A] truncate">{user.name}</p>
                        <p className="text-sm text-[#5C5C5C]">{user.department}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-[#059669]">
                          Score: {user.last_session_score || 'N/A'}
                        </p>
                        <p className="text-xs text-[#9CA3AF]">
                          {user.last_session_date ? new Date(user.last_session_date).toLocaleDateString() : ''}
                        </p>
                      </div>
                      <Link to={`/admin/users/${user.id}`}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#059669] hover:bg-[#059669]/10 rounded-full"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <AlertTriangle className="w-12 h-12 text-[#D97706] mx-auto mb-4" />
                  <p className="text-[#1A1A1A] font-semibold">No compliant users yet</p>
                  <p className="text-sm text-[#5C5C5C]">Users will appear here after completing sessions</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
